import { normalizePhone } from './contacts.ts';
import { z } from 'zod';

// Authentication request bodies and password rules, shared by API and web app.

export const PASSWORD_MIN_LENGTH = 12;
/** Upper bound so a huge "password" cannot be used to burn CPU in Argon2id. */
export const PASSWORD_MAX_LENGTH = 128;

/** Recovery code alphabet: no 0/O or 1/I to avoid transcription mistakes. */
export const RECOVERY_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const RECOVERY_CODE_LENGTH = 10;
export const RECOVERY_CODE_COUNT = 10;

const email = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email('Correo no válido').max(254, 'Correo demasiado largo'));

export const loginSchema = z.strictObject({
  email,
  // Length rules apply when a password is created, not when it is typed to log in.
  password: z
    .string()
    .min(1, 'Escribe tu contraseña')
    .max(PASSWORD_MAX_LENGTH * 2),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

export const preferencesSchema = z.strictObject({
  theme: z.enum(THEME_PREFERENCES, 'Tema no válido'),
});
export type PreferencesInput = z.infer<typeof preferencesSchema>;

export const totpCodeSchema = z.strictObject({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'El código tiene 6 dígitos'),
});
export type TotpCodeInput = z.infer<typeof totpCodeSchema>;

/** The login's 2FA step: the code, and whether to remember this browser. */
export const verifyCodeSchema = totpCodeSchema.extend({
  rememberDevice: z.boolean().default(false),
});
export type VerifyCodeInput = z.infer<typeof verifyCodeSchema>;

/** Terms an agency may choose for "Recordar este dispositivo" (null: off). */
export const TRUSTED_DEVICE_DAYS = [14, 30] as const;
export type TrustedDeviceDays = (typeof TRUSTED_DEVICE_DAYS)[number];

/** "Cerrar las demás sesiones" (the web app checks forgetDevices by default). */
export const revokeOtherSessionsSchema = z.preprocess(
  (value) => value ?? {},
  z.strictObject({ forgetDevices: z.boolean().default(false) }),
);
export type RevokeOtherSessionsInput = z.infer<typeof revokeOtherSessionsSchema>;

/** Accepts "ABCDE-FGHJK", lower case or with spaces; normalizes to 10 characters. */
export const recoveryCodeSchema = z.strictObject({
  code: z
    .string()
    .transform((v) => v.toUpperCase().replace(/[^A-Z0-9]/g, ''))
    .pipe(z.string().length(RECOVERY_CODE_LENGTH, 'Código de respaldo no válido')),
});
export type RecoveryCodeInput = z.infer<typeof recoveryCodeSchema>;

export const createInvitationSchema = z.strictObject({
  email,
  roleId: z.uuid('Rol no válido'),
  teamId: z.uuid('Equipo no válido').nullable().optional(),
});
export type CreateInvitationInput = z.infer<typeof createInvitationSchema>;

const token = z.string().trim().min(20, 'Enlace no válido').max(200, 'Enlace no válido');

export const invitationTokenSchema = z.strictObject({ token });
export type InvitationTokenInput = z.infer<typeof invitationTokenSchema>;

export const acceptInvitationSchema = z.strictObject({
  token,
  fullName: z
    .string()
    .trim()
    .min(1, 'Escribe tu nombre')
    .max(200, 'El nombre no puede tener más de 200 caracteres'),
  // The full password policy (length, leaked list...) is checked by the API, which
  // also knows the email and the tenant's name.
  password: z.string().max(PASSWORD_MAX_LENGTH, `Máximo ${String(PASSWORD_MAX_LENGTH)} caracteres`),
});
export type AcceptInvitationInput = z.infer<typeof acceptInvitationSchema>;

export const securitySettingsSchema = z
  .strictObject({
    mfaRequired: z.boolean().optional(),
    trustedDeviceDays: z
      .union([z.literal(14), z.literal(30), z.null()], 'Elige 14 o 30 días, o desactívalo')
      .optional(),
  })
  .refine((v) => v.mfaRequired !== undefined || v.trustedDeviceDays !== undefined, {
    message: 'Nada que cambiar',
  });
export type SecuritySettingsInput = z.infer<typeof securitySettingsSchema>;

/** Length in characters (code points), as PostgreSQL's length() counts: an emoji is 1. */
const chars = (value: string) => Array.from(value).length;

export const startImpersonationSchema = z.strictObject({
  membershipId: z.uuid('Miembro no válido'),
  reason: z
    .string()
    .trim()
    .refine((v) => chars(v) >= 15, 'Explica el motivo (al menos 15 caracteres)')
    .refine((v) => chars(v) <= 500, 'El motivo no puede tener más de 500 caracteres'),
});
export type StartImpersonationInput = z.infer<typeof startImpersonationSchema>;

export const passwordResetRequestSchema = z.strictObject({ email });
export type PasswordResetRequestInput = z.infer<typeof passwordResetRequestSchema>;

/** A token from an emailed link plus a candidate password (reset or invitation). */
export const tokenPasswordSchema = z.strictObject({
  token,
  password: z.string().max(PASSWORD_MAX_LENGTH, `Máximo ${String(PASSWORD_MAX_LENGTH)} caracteres`),
});
export type TokenPasswordInput = z.infer<typeof tokenPasswordSchema>;

export const changePasswordSchema = z.strictObject({
  currentPassword: z
    .string()
    .min(1, 'Escribe tu contraseña actual')
    .max(PASSWORD_MAX_LENGTH * 2),
  newPassword: z
    .string()
    .max(PASSWORD_MAX_LENGTH, `Máximo ${String(PASSWORD_MAX_LENGTH)} caracteres`),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const invitationPasswordCheckSchema = z.strictObject({
  token,
  password: z.string().max(PASSWORD_MAX_LENGTH, `Máximo ${String(PASSWORD_MAX_LENGTH)} caracteres`),
  fullName: z.string().trim().max(200).optional(),
});
export type InvitationPasswordCheckInput = z.infer<typeof invitationPasswordCheckSchema>;

/** A phone for clients: Salvadoran 8 digits or with country code; empty removes it. */
const clientPhone = z
  .string()
  .trim()
  .max(30)
  .transform((value, ctx) => {
    if (value === '') return null;
    const normalized = normalizePhone(value);
    if (!normalized) {
      ctx.addIssue({
        code: 'custom',
        message: 'Teléfono no válido: 8 dígitos de El Salvador, o con código de país (+1 555...)',
      });
      return z.NEVER;
    }
    return normalized;
  })
  .nullable();

/**
 * The member's own profile: work phone shown to clients (public listing, WhatsApp)
 * and whether they get the daily summary email (HU-AGE-13).
 */
export const profileSchema = z
  .strictObject({ workPhone: clientPhone.optional(), dailySummary: z.boolean().optional() })
  .refine((v) => v.workPhone !== undefined || v.dailySummary !== undefined, 'No hay cambios');
export type ProfileInput = z.input<typeof profileSchema>;

/** Agency settings: general phone/WhatsApp, used when the agent has none. */
export const agencySettingsSchema = z.strictObject({ publicPhone: clientPhone });
export type AgencySettingsInput = z.input<typeof agencySettingsSchema>;
